// /app/frontend/src/pages/public/CouponRedirect.js
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiTag, FiArrowRight, FiAlertTriangle, FiCheck } from 'react-icons/fi';
import api from '@/services/api';

const CouponRedirect = () => {
  const { code } = useParams();
  const navigate = useNavigate();
  const [coupon, setCoupon] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadCoupon();
  }, [code]);

  const loadCoupon = async () => {
    try {
      const response = await api.get(`/coupons/freight/${code}`);
      setCoupon(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Cupom não encontrado');
    } finally {
      setLoading(false);
    }
  };

  const getDiscountColor = (percent) => {
    if (percent === 100) return 'from-green-500 to-green-600';
    if (percent >= 70) return 'from-blue-500 to-blue-600';
    if (percent >= 50) return 'from-yellow-500 to-yellow-600';
    return 'from-gray-500 to-gray-600';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-red-500 border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-600">Verificando cupom...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <FiAlertTriangle className="text-red-500 text-3xl" />
          </div>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Cupom Inválido</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors"
          >
            Ir para o Início
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl overflow-hidden max-w-md w-full">
        {/* Header colorido */}
        <div className={`bg-gradient-to-r ${getDiscountColor(coupon.discount_percent)} p-8 text-white text-center`}>
          <FiTag className="mx-auto text-5xl mb-4" />
          <h1 className="text-4xl font-bold mb-2">{coupon.discount_percent}% OFF</h1>
          <p className="text-lg opacity-90">no valor do frete!</p>
        </div>

        {/* Conteúdo */}
        <div className="p-6">
          {/* Código */}
          <div className="bg-gray-100 rounded-xl p-4 text-center mb-6">
            <p className="text-sm text-gray-500 mb-1">Código do cupom</p>
            <p className="text-3xl font-mono font-bold text-gray-800">{coupon.code}</p>
          </div>

          {/* Informações */}
          <div className="space-y-3 mb-6">
            {coupon.store_name ? (
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500">Válido na loja</span>
                <span className="font-medium text-gray-800">{coupon.store_name}</span>
              </div>
            ) : (
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500">Válido em</span>
                <span className="font-medium text-green-600">Todas as lojas</span>
              </div>
            )}
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-500">Válido até</span>
              <span className="font-medium text-gray-800">
                {new Date(coupon.expires_at).toLocaleDateString('pt-BR')}
              </span>
            </div>
            {coupon.max_uses && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500">Usos restantes</span>
                <span className="font-medium text-gray-800">
                  {coupon.max_uses - coupon.uses_count}
                </span>
              </div>
            )}
          </div>

          {/* Benefícios */}
          <div className="bg-green-50 rounded-xl p-4 mb-6">
            <h3 className="font-medium text-green-800 mb-2">O que você ganha:</h3>
            <ul className="space-y-2 text-sm text-green-700">
              <li className="flex items-center gap-2">
                <FiCheck className="text-green-500" />
                {coupon.discount_percent === 100 
                  ? 'Frete totalmente grátis!'
                  : `${coupon.discount_percent}% de desconto no frete`
                }
              </li>
              <li className="flex items-center gap-2">
                <FiCheck className="text-green-500" />
                Desconto aplicado automaticamente no checkout
              </li>
            </ul>
          </div>

          {/* Botão de ação */}
          {coupon.store_id ? (
            <button
              onClick={() => navigate(`/loja/${coupon.store_id}?cupom=${coupon.code}`)}
              className="w-full py-4 bg-red-600 text-white rounded-xl font-semibold hover:bg-red-700 transition-colors flex items-center justify-center gap-2"
              data-testid="go-to-store-btn"
            >
              Ir para a Loja
              <FiArrowRight />
            </button>
          ) : (
            <button
              onClick={() => navigate(`/?cupom=${coupon.code}`)}
              className="w-full py-4 bg-red-600 text-white rounded-xl font-semibold hover:bg-red-700 transition-colors flex items-center justify-center gap-2"
              data-testid="browse-stores-btn"
            >
              Ver Lojas Disponíveis
              <FiArrowRight />
            </button>
          )}

          <p className="text-xs text-gray-400 text-center mt-4">
            O cupom será aplicado automaticamente no checkout
          </p>
        </div>
      </div>
    </div>
  );
};

export default CouponRedirect;
